import type { MappingResponse, SchemaSetSnapshot } from '../../services/api';
import {
  CANVAS_GROUP_BODY_PADDING,
  CANVAS_GROUP_HEADER_HEIGHT,
  CANVAS_GROUP_VERTICAL_GAP,
  CANVAS_GROUP_WIDTH,
  CANVAS_MIN_GROUP_HEIGHT,
  CANVAS_ROW_SPACING,
  CANVAS_SOURCE_GROUP_X,
  CANVAS_TARGET_GROUP_X,
} from '../layoutConstants';
import { buildQualified, deriveGroupLabel, normaliseDisplayLabel } from './qualifiedNames';
import {
  collectFieldsFromSnapshot,
  collectTargetDisplayNames,
  extractFileName,
  getNormalizedSourceFields,
  getNormalizedTargetField,
  resolveSchemaTableKey,
} from './schemaResolution';
import { tablesMatch } from '../utils/tableMatching';
import type { DetailTableFilter, GroupLayout, LayoutContext, OriginGroupLayout } from './types';

const isSnapshotSourceAlreadyRepresented = (
  snapshotKey: string,
  mappedAliases: string[],
  sourceSnapshot?: SchemaSetSnapshot,
  sourceAliasMap?: Record<string, unknown>,
): boolean => {
  return mappedAliases.some((alias) => {
    if (tablesMatch(alias, snapshotKey)) return true;
    const resolved = resolveSchemaTableKey(alias, sourceSnapshot, sourceAliasMap);
    if (resolved && tablesMatch(resolved, snapshotKey)) return true;
    const fileName = sourceAliasMap ? extractFileName(sourceAliasMap[alias]) : undefined;
    if (fileName && tablesMatch(fileName, snapshotKey)) return true;
    return false;
  });
};

const ensureDetailTableInSourceWeights = (
  detailFilter: DetailTableFilter,
  sourceFieldWeights: Map<string, Map<string, number>>,
  sourceGroupOrder: Map<string, number>,
  sourceSnapshot?: SchemaSetSnapshot,
  sourceAliasMap?: Record<string, unknown>,
): void => {
  const alreadyPresent = Array.from(sourceFieldWeights.keys()).some((k) =>
    tablesMatch(k, detailFilter.tableId),
  );
  if (alreadyPresent) return;

  const resolvedKey =
    resolveSchemaTableKey(detailFilter.tableId, sourceSnapshot, sourceAliasMap)
    ?? Object.keys(sourceSnapshot?.tables ?? {}).find((k) => tablesMatch(k, detailFilter.tableId));

  const key = resolvedKey ?? detailFilter.tableId;
  sourceFieldWeights.set(key, new Map<string, number>());
  sourceGroupOrder.set(key, Number.MAX_SAFE_INTEGER);
};

const ensureDetailTableInTargetOrder = (
  detailFilter: DetailTableFilter,
  targetFieldOrder: Map<string, Map<string, number>>,
  targetGroupOrder: Map<string, number>,
  targetSnapshot?: SchemaSetSnapshot,
  targetAliasMap?: Record<string, string>,
): void => {
  const alreadyPresent = Array.from(targetFieldOrder.keys()).some((k) =>
    tablesMatch(k, detailFilter.tableId),
  );
  if (alreadyPresent) return;

  const resolvedKey =
    resolveSchemaTableKey(detailFilter.tableId, targetSnapshot, targetAliasMap)
    ?? Object.keys(targetSnapshot?.tables ?? {}).find((k) => tablesMatch(k, detailFilter.tableId));

  const key = resolvedKey ?? detailFilter.tableId;
  targetFieldOrder.set(key, new Map<string, number>());
  targetGroupOrder.set(key, Number.MAX_SAFE_INTEGER);
};

/** Collapse mapping aliases that refer to the same logical source table. */
const mergeMatchingSourceAliases = (
  sourceFieldWeights: Map<string, Map<string, number>>,
  sourceGroupOrder: Map<string, number>,
  sourceSnapshot?: SchemaSetSnapshot,
  sourceAliasMap?: Record<string, unknown>,
): void => {
  const keys = Array.from(sourceFieldWeights.keys());
  for (let i = 0; i < keys.length; i++) {
    const canonical = keys[i];
    if (!sourceFieldWeights.has(canonical)) continue;

    for (let j = i + 1; j < keys.length; j++) {
      const duplicate = keys[j];
      if (!sourceFieldWeights.has(duplicate)) continue;

      const sameTable =
        tablesMatch(canonical, duplicate)
        || isSnapshotSourceAlreadyRepresented(duplicate, [canonical], sourceSnapshot, sourceAliasMap)
        || isSnapshotSourceAlreadyRepresented(canonical, [duplicate], sourceSnapshot, sourceAliasMap);
      if (!sameTable) continue;

      const canonicalWeights = sourceFieldWeights.get(canonical)!;
      const duplicateWeights = sourceFieldWeights.get(duplicate)!;
      duplicateWeights.forEach((weight, field) => {
        const current = canonicalWeights.get(field);
        if (current === undefined || weight < current) {
          canonicalWeights.set(field, weight);
        }
      });

      const canonicalOrder = sourceGroupOrder.get(canonical) ?? Number.MAX_SAFE_INTEGER;
      const duplicateOrder = sourceGroupOrder.get(duplicate) ?? Number.MAX_SAFE_INTEGER;
      if (duplicateOrder < canonicalOrder) {
        sourceGroupOrder.set(canonical, duplicateOrder);
      }

      sourceFieldWeights.delete(duplicate);
      sourceGroupOrder.delete(duplicate);
      keys.splice(j, 1);
      j -= 1;
    }
  }
};

export const collectGroupLayouts = (
  response: MappingResponse,
  expandedGroups?: Set<string>,
  focusActive?: boolean,
  detailFilter?: DetailTableFilter | null,
): LayoutContext => {
  const sourceFieldWeights = new Map<string, Map<string, number>>();
  const targetFieldOrder = new Map<string, Map<string, number>>();
  const targetGroupOrder = new Map<string, number>();
  const sourceGroupOrder = new Map<string, number>();
  const targetDisplayNames = collectTargetDisplayNames(response);

  // Determine the source schema snapshot.
  const meta = response.metadata;
  let sourceSnapshot: SchemaSetSnapshot | undefined = meta?.source_schema_set_snapshot;
  if (!sourceSnapshot && meta?.source_schema_signature) {
    const signature = meta.source_schema_signature;
    if (signature.sources && Array.isArray(signature.sources)) {
      sourceSnapshot = {
        name: 'source-signature',
        status: 'active',
        tables: signature.sources.reduce<Record<string, { name: string; display_name: string; columns: typeof signature.sources[0]['columns'] }>>((acc, src) => {
          acc[src.source] = {
            name: src.source,
            display_name: src.source,
            columns: src.columns,
          };
          return acc;
        }, {}),
      };
    }
  }

  const sourceAliasMap = meta?.source_alias_map;
  const targetSnapshot = response.transformation_plan?.target_schema_snapshot
    ?? meta?.target_schema_snapshot
    ?? meta?.target_schema_snapshot
    ?? meta?.schema_context?.target_schema_snapshot;
  const targetAliasMap = meta?.schema_alias_map?.tables;

  response.mappings?.forEach((mapping, index) => {
    const table = (mapping.target_table ?? 'target').trim() || 'target';
    const field = getNormalizedTargetField(mapping, targetSnapshot, targetAliasMap);
    if (!field) {
      return;
    }
    const fieldsForTable = targetFieldOrder.get(table) ?? new Map<string, number>();
    if (!fieldsForTable.has(field)) {
      fieldsForTable.set(field, index);
    }
    targetFieldOrder.set(table, fieldsForTable);
    const currentOrder = targetGroupOrder.get(table);
    if (currentOrder === undefined || index < currentOrder) {
      targetGroupOrder.set(table, index);
    }
  });

  response.mappings?.forEach((mapping, index) => {
    const alias = (mapping.source_file ?? 'source').trim() || 'source';
    const field = (mapping.source_field ?? '').trim();
    if (!field) {
      return;
    }
    const targetGroupLabel = (mapping.target_table ?? 'target').trim() || 'target';
    const targetWeights = targetFieldOrder.get(targetGroupLabel);

    const targetField = normaliseDisplayLabel(
      getNormalizedTargetField(mapping, targetSnapshot, targetAliasMap)
    );
    const weight = targetField ? targetWeights?.get(targetField) ?? index : index;
    const fieldWeights = sourceFieldWeights.get(alias) ?? new Map<string, number>();

    const fields = getNormalizedSourceFields(mapping, sourceSnapshot, sourceAliasMap);

    fields.forEach(field => {
      const currentWeight = fieldWeights.get(field);
      if (currentWeight === undefined || weight < currentWeight) {
        fieldWeights.set(field, weight);
      }
    });
    sourceFieldWeights.set(alias, fieldWeights);
    const currentGroupWeight = sourceGroupOrder.get(alias);
    if (currentGroupWeight === undefined || weight < currentGroupWeight) {
      sourceGroupOrder.set(alias, weight);
    }
  });

  mergeMatchingSourceAliases(sourceFieldWeights, sourceGroupOrder, sourceSnapshot, sourceAliasMap);

  const restrictSnapshotOrphans = focusActive || !!detailFilter;

  // Ensure source tables from the snapshot that have no mappings are still
  // represented. Skip unrelated tables during focus / detail drill-down.
  if (sourceSnapshot?.tables && !restrictSnapshotOrphans) {
    const mappedAliases = Array.from(sourceFieldWeights.keys());
    for (const key of Object.keys(sourceSnapshot.tables)) {
      if (sourceFieldWeights.has(key)) continue;
      if (isSnapshotSourceAlreadyRepresented(key, mappedAliases, sourceSnapshot, sourceAliasMap)) {
        continue;
      }
      sourceFieldWeights.set(key, new Map<string, number>());
      sourceGroupOrder.set(key, Number.MAX_SAFE_INTEGER);
    }
  } else if (detailFilter?.role === 'source') {
    ensureDetailTableInSourceWeights(
      detailFilter,
      sourceFieldWeights,
      sourceGroupOrder,
      sourceSnapshot,
      sourceAliasMap,
    );
  }

  // Ensure target tables from the snapshot that have no mappings are still
  // represented. Skip unrelated tables during focus / detail drill-down.
  if (targetSnapshot?.tables && !restrictSnapshotOrphans) {
    for (const key of Object.keys(targetSnapshot.tables)) {
      if (!targetFieldOrder.has(key)) {
        targetFieldOrder.set(key, new Map<string, number>());
        targetGroupOrder.set(key, Number.MAX_SAFE_INTEGER);
      }
    }
  } else if (detailFilter?.role === 'target') {
    ensureDetailTableInTargetOrder(
      detailFilter,
      targetFieldOrder,
      targetGroupOrder,
      targetSnapshot,
      targetAliasMap,
    );
  }

  const sourceGroups = new Map<string, GroupLayout>();
  const targetGroups = new Map<string, GroupLayout>();
  const sourceFieldIndex = new Map<string, number>();
  const targetFieldIndex = new Map<string, number>();

  let currentSourceY = 40;
  Array.from(sourceFieldWeights.entries())
    .sort(([aliasA], [aliasB]) => {
      const weightA = sourceGroupOrder.get(aliasA) ?? Number.MAX_SAFE_INTEGER;
      const weightB = sourceGroupOrder.get(aliasB) ?? Number.MAX_SAFE_INTEGER;
      if (weightA === weightB) {
        return aliasA.localeCompare(aliasB);
      }
      return weightA - weightB;
    })
    .forEach(([alias, fieldWeights]) => {
      const orderedFields = Array.from(fieldWeights.entries())
        .sort(([fieldA, weightA], [fieldB, weightB]) => {
          if (weightA === weightB) {
            return fieldA.localeCompare(fieldB);
          }
          return weightA - weightB;
        })
        .map(([field]) => field);

      const localSourceAliasMap = response.metadata?.source_alias_map;
      const allSchemaFields = collectFieldsFromSnapshot(alias, sourceSnapshot, localSourceAliasMap);

      const mappedSet = new Set(orderedFields.map(f => f.toLowerCase()));
      const unmappedFields = allSchemaFields.filter(f => !mappedSet.has(f.toLowerCase()));

      let effectiveFields = [...orderedFields, ...unmappedFields];

      const expanded = expandedGroups?.has(`group:source:${alias}`) ?? false;

      if (effectiveFields.length === 0 && orderedFields.length === 0) effectiveFields = [''];

      const totalFieldCount = effectiveFields.length;
      const mappedCount = orderedFields.length;
      const height = expanded
        ? Math.max(
            CANVAS_MIN_GROUP_HEIGHT,
            CANVAS_GROUP_HEADER_HEIGHT + CANVAS_GROUP_BODY_PADDING * 2 + effectiveFields.length * CANVAS_ROW_SPACING
          )
        : CANVAS_GROUP_HEADER_HEIGHT + 16;
      const id = `group:source:${alias}`;
      const label = alias || 'Source';
      sourceGroups.set(alias, {
        id,
        label: deriveGroupLabel(label, 'Source'),
        originalLabel: alias,
        role: 'source',
        x: CANVAS_SOURCE_GROUP_X,
        y: currentSourceY,
        height,
        expanded,
        mappedCount,
        totalFieldCount,
      });
      if (expanded) {
        effectiveFields.forEach((field, index) => {
          if (field) {
            sourceFieldIndex.set(buildQualified(alias, field, alias), index);
          }
        });
      }
      currentSourceY += height + CANVAS_GROUP_VERTICAL_GAP;
    });

  let currentTargetY = 40;
  Array.from(targetFieldOrder.entries())
    .sort(([tableA], [tableB]) => {
      const weightA = targetGroupOrder.get(tableA) ?? Number.MAX_SAFE_INTEGER;
      const weightB = targetGroupOrder.get(tableB) ?? Number.MAX_SAFE_INTEGER;
      if (weightA === weightB) {
        return tableA.localeCompare(tableB);
      }
      return weightA - weightB;
    })
    .forEach(([table, fieldOrder]) => {
      const mappedFields = Array.from(fieldOrder.entries())
        .sort(([fieldA, weightA], [fieldB, weightB]) => {
          if (weightA === weightB) {
            return fieldA.localeCompare(fieldB);
          }
          return weightA - weightB;
        })
        .map(([field]) => field);

      const expanded = expandedGroups?.has(`group:target:${table}`) ?? false;

      const allSchemaFields = collectFieldsFromSnapshot(table, targetSnapshot, targetAliasMap);

      const mappedSet = new Set(mappedFields.map(f => f.toLowerCase()));
      const unmappedFields = allSchemaFields.filter(f => !mappedSet.has(f.toLowerCase()));

      const displayFields = [...mappedFields, ...unmappedFields];

      const effectiveFields = displayFields.length ? displayFields : [''];

      const totalFieldCount = effectiveFields.length;
      const mappedCount = mappedFields.length;
      const height = expanded
        ? Math.max(
            CANVAS_MIN_GROUP_HEIGHT,
            CANVAS_GROUP_HEADER_HEIGHT + CANVAS_GROUP_BODY_PADDING * 2 + effectiveFields.length * CANVAS_ROW_SPACING
          )
        : CANVAS_GROUP_HEADER_HEIGHT + 16;
      const id = `group:target:${table}`;
      const label = table || 'Target';
      const friendlyLabel = targetDisplayNames.get(table) ?? deriveGroupLabel(label, 'Target');
      targetGroups.set(table, {
        id,
        label: friendlyLabel,
        originalLabel: table,
        role: 'target',
        x: CANVAS_TARGET_GROUP_X,
        y: currentTargetY,
        height,
        expanded,
        mappedCount,
        totalFieldCount,
      });
      if (expanded) {
        effectiveFields.forEach((field, index) => {
          if (field) {
            targetFieldIndex.set(buildQualified(table, field, table), index);
          }
        });
      }
      currentTargetY += height + CANVAS_GROUP_VERTICAL_GAP;
    });

  // ---- Phase 4a: Build origin groups by detecting :: namespace separator ----
  const originGroups = new Map<string, OriginGroupLayout>();
  const originChildren = new Map<string, string[]>();

  sourceGroups.forEach((group, alias) => {
    const sepIdx = alias.indexOf('::');
    const originKey = sepIdx > 0 ? alias.substring(0, sepIdx) : '__file_uploads__';
    group.originGroupKey = originKey;
    const children = originChildren.get(originKey) ?? [];
    children.push(alias);
    originChildren.set(originKey, children);
  });

  if (originChildren.size > 1) {
    const ORIGIN_HEADER_HEIGHT = CANVAS_GROUP_HEADER_HEIGHT;
    const ORIGIN_PADDING = 8;
    let originY = 40;

    originChildren.forEach((childAliases, originKey) => {
      const isFileUploads = originKey === '__file_uploads__';
      const originLabel = isFileUploads ? 'File Uploads' : originKey.replace(/-/g, ' ');
      const originExpanded = expandedGroups?.has(`origin:${originKey}`) ?? (childAliases.length <= 2);

      let childMappedFields = 0;
      let childTotalFields = 0;

      if (originExpanded) {
        let childY = ORIGIN_HEADER_HEIGHT + ORIGIN_PADDING;
        childAliases.forEach((alias) => {
          const group = sourceGroups.get(alias)!;
          group.y = originY + childY;
          childMappedFields += group.mappedCount ?? 0;
          childTotalFields += group.totalFieldCount ?? 0;
          childY += group.height + CANVAS_GROUP_VERTICAL_GAP;
        });

        const totalHeight = childY + ORIGIN_PADDING;
        originGroups.set(originKey, {
          id: `origin:${originKey}`,
          label: originLabel,
          originType: isFileUploads ? 'file_upload' : 'connector',
          x: CANVAS_SOURCE_GROUP_X - 12,
          y: originY,
          width: CANVAS_GROUP_WIDTH + 24,
          height: totalHeight,
          expanded: true,
          childTableCount: childAliases.length,
          childMappedFields,
          childTotalFields,
        });
        originY += totalHeight + CANVAS_GROUP_VERTICAL_GAP;
      } else {
        childAliases.forEach((alias) => {
          const group = sourceGroups.get(alias)!;
          childMappedFields += group.mappedCount ?? 0;
          childTotalFields += group.totalFieldCount ?? 0;
          group.y = originY + ORIGIN_HEADER_HEIGHT + ORIGIN_PADDING;
        });

        const collapsedHeight = ORIGIN_HEADER_HEIGHT + 16;
        originGroups.set(originKey, {
          id: `origin:${originKey}`,
          label: originLabel,
          originType: isFileUploads ? 'file_upload' : 'connector',
          x: CANVAS_SOURCE_GROUP_X - 12,
          y: originY,
          width: CANVAS_GROUP_WIDTH + 24,
          height: collapsedHeight,
          expanded: false,
          childTableCount: childAliases.length,
          childMappedFields,
          childTotalFields,
        });
        originY += collapsedHeight + CANVAS_GROUP_VERTICAL_GAP;
      }
    });
  }

  return { sourceGroups, targetGroups, sourceFieldIndex, targetFieldIndex, originGroups };
};
